// `/api3/company/permissions` answers with the catalog (sections and their
// permissions) and the enabled state apart, and its labels are written without
// a subject ("puede(n) borrar…") so both the global tab and a permission group
// can reuse them. The global tab renders one flat list per section with the
// subject spelled out, so both halves are merged here.
const GLOBAL_SUBJECT = 'Todos los agentes';
const SCREEN_HINT = /\s*\[[^\]]*\]\s*$/;
const CONTROL_LISTING_SECTIONS = ['properties', 'developments'];

export const toGlobalPermissionLabel = (label = '') => {
  const action = label
    .replace(SCREEN_HINT, '')
    .replace(/^puede\(n\)/, 'pueden')
    .replace(/^puede\b/, 'pueden');

  return `${GLOBAL_SUBJECT} ${action}`;
};

const toEnabledById = (globalSecurityPermissions) => {
  const enabledById = new Map();

  Object.values(globalSecurityPermissions).forEach((permissions) => {
    permissions.forEach(({ id, enabled }) => {
      enabledById.set(id, Boolean(enabled));
    });
  });

  return enabledById;
};

// Shared by the global tab and a group detail: both walk the same
// permission_catalog shape and only differ in how a permission's
// enabled/disabled state is resolved for the principal being rendered.
const buildPermissionGroups = (
  permissionCatalog,
  controlListingEditionActive,
  adaptPermission
) => {
  const isVisible = ({
    hide_under_control_listing_edition_active: hiddenByControlListing,
  }) => !(hiddenByControlListing && controlListingEditionActive);

  return Object.values(permissionCatalog)
    .map((section) => ({
      id: section.section_key,
      name: section.section_label,
      affectedByControlListing:
        controlListingEditionActive &&
        CONTROL_LISTING_SECTIONS.includes(section.section_key),
      permissions: (section.permissions || [])
        .filter(isVisible)
        .map(adaptPermission),
    }))
    .filter((section) => section.permissions.length > 0);
};

export const adaptCompanyPermissions = (response = {}) => {
  const {
    permission_catalog: permissionCatalog = {},
    global_security_permissions: globalSecurityPermissions = {},
    control_listing_edition: controlListingEdition = {},
  } = response;
  const enabledById = toEnabledById(globalSecurityPermissions);
  const controlListingEditionActive = Boolean(controlListingEdition.active);

  const permissionGroups = buildPermissionGroups(
    permissionCatalog,
    controlListingEditionActive,
    (permission) => ({
      id: permission.id,
      label: toGlobalPermissionLabel(permission.label),
      description: permission.massive_edit_help || null,
      enabled: enabledById.get(permission.id) || false,
      disabled: false,
      ...(permission.parent_permission_id
        ? { depends_on: permission.parent_permission_id }
        : {}),
    })
  );

  return { permission_groups: permissionGroups };
};

// `permissions` on the group detail response is keyed by section_key, each
// entry a list of {id, enabled, disabled?} — the group's own toggle states,
// separate from the shared permission_catalog labels.
const toGroupPermissionStates = (permissionsBySection = {}) => {
  const states = new Map();

  Object.values(permissionsBySection).forEach((items) => {
    items.forEach(({ id, enabled, disabled }) => {
      states.set(id, {
        enabled: Boolean(enabled),
        disabled: Boolean(disabled),
      });
    });
  });

  return states;
};

export const adaptPermissionGroupDetail = (response = {}) => {
  const {
    id,
    name,
    branches_count: branchesCount,
    teams_count: teamsCount,
    agents_count: agentsCount,
    permission_catalog: permissionCatalog = {},
    permissions: permissionsBySection = {},
    control_listing_edition: controlListingEdition = {},
  } = response;
  const states = toGroupPermissionStates(permissionsBySection);
  const controlListingEditionActive = Boolean(controlListingEdition.active);

  const permissionGroups = buildPermissionGroups(
    permissionCatalog,
    controlListingEditionActive,
    (permission) => {
      const state = states.get(permission.id) || {};

      return {
        id: permission.id,
        label: toGlobalPermissionLabel(permission.label),
        description: permission.massive_edit_help || null,
        enabled: state.enabled || false,
        disabled: state.disabled || false,
        ...(permission.parent_permission_id
          ? { depends_on: permission.parent_permission_id }
          : {}),
      };
    }
  );

  return {
    id,
    name,
    branches_count: branchesCount,
    teams_count: teamsCount,
    agents_count: agentsCount,
    permission_groups: permissionGroups,
  };
};
